/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../../assets/icons/clock-v2-icon.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as CreditCardIcon } from '../../../assets/icons/credit-card.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as EyeIcon } from '../../../assets/icons/eye-icon.svg';
import { ReactComponent as EyeSlashIcon } from '../../../assets/icons/eye-slash.svg';
import { ReactComponent as ApplePayLogo } from '../../../assets/icons/payment-apple.svg';
import { ReactComponent as MastercardLogo } from '../../../assets/icons/payment-mastercard.svg';
import { ReactComponent as PayPalLogo } from '../../../assets/icons/payment-paypal.svg';
import { ReactComponent as StripeLogo } from '../../../assets/icons/payment-stripe.svg';
import { ReactComponent as VisaLogo } from '../../../assets/icons/payment-visa.svg';
import { ReactComponent as ExpandIcon } from '../../../assets/icons/tables/expand-icon.svg';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SwitchButton from '../../../shared-components/switch-button-field/SwitchButton';
import { getCountryName } from '../../../shared-functions/CountryCodes';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import paymentGatewayApi from '../../../utils/api/v2/payment-gateway';

interface GatewayListApi {
  gatewayKey: string;
  paymentMethods: string[];
  useCases: string[];
  currency: string;
  currencies?: string[];
  platformFee?: number | string;
  platformFeeByCurrency?: { currency: string; amount: number | string }[];
  platformFeeType?: 'percentage' | 'currency' | 'amount';
  paymentSurcharge?: number | string;
  paymentSurchargeType?: 'percentage';
  country: string;
  isActive: boolean;
  activeUseCases?: string[];
  credentials: {
    apiEndpoint?: string;
    bearerToken?: string;
    initialEntityId?: string;
    repeatedEntityId?: string;
    secret_key?: string;
    publishable_key?: string;
    webhook_secret?: string;
    [key: string]: any;
  };
  updatedAt: string;
  configId?: string;
  isDefault?: boolean;
  defaultCurrency?: string;
}

interface ViewGatewayProperties {
  gateway: GatewayListApi;
  onEdit: () => void;
  onClose: () => void;
  onStatusChange?: () => void;
}

/**
 * Use Case Label helper
 * @param {string} useCase - The useCase
 * @param {string} defaultValue - The defaultValue
 * @param {any} t - The translation function
 * @returns {string} The translated use case label string
 */
const getUseCaseLabel = (useCase: string, defaultValue: string, t: any): string => {
  const lower = useCase.toLowerCase();
  if (lower === 'b2c') return t('b2cPayment');
  if (lower === 'b2b') return t('b2bPayout');
  return defaultValue;
};

/**
 * Mada logo component
 * @returns {any} React node
 */
function MadaLogoComponent() {
  return (
    <div className="mada-logo-inline method-icon" title="Mada">
      mada
    </div>
  );
}

/**
 * Render payment method logo icons
 * @param {any} methods - The methods
 */
const renderPaymentMethods = (methods: string[]) => {
  const formattedMethods = methods.map((m) => m.toLowerCase());

  const icons: React.ReactNode[] = [];

  // 1. Mada
  if (formattedMethods.includes('mada')) {
    icons.push(<MadaLogoComponent key="mada" />);
  }

  // 2. Debit card / Visa / Mastercard group
  if (
    formattedMethods.includes('debit_card') ||
    formattedMethods.includes('debit card') ||
    formattedMethods.includes('visa')
  ) {
    icons.push(
      <span key="visa" title="Visa" className="method-icon-tooltip">
        <VisaLogo className="method-icon" />
      </span>,
    );
    icons.push(
      <span key="mastercard" title="Mastercard" className="method-icon-tooltip">
        <MastercardLogo className="method-icon" />
      </span>,
    );
  }

  // 3. PayPal
  if (formattedMethods.includes('paypal')) {
    icons.push(
      <span key="paypal" title="PayPal" className="method-icon-tooltip">
        <PayPalLogo className="method-icon" />
      </span>,
    );
  }

  // 4. Apple Pay
  if (formattedMethods.includes('apple_pay') || formattedMethods.includes('apple pay')) {
    icons.push(
      <span key="apple" title="Apple Pay" className="method-icon-tooltip">
        <ApplePayLogo className="method-icon" />
      </span>,
    );
  }

  // 5. Credit Card (outline logo)
  if (formattedMethods.includes('credit_card') || formattedMethods.includes('credit card')) {
    icons.push(
      <span key="credit_card" title="Credit Card" className="method-icon-tooltip">
        <CreditCardIcon className="method-icon card-outline-icon" />
      </span>,
    );
  }

  // 6. Dynamic fallback for other methods
  const standardLogoMethods = [
    'credit_card',
    'credit card',
    'debit_card',
    'debit card',
    'mada',
    'visa',
    'paypal',
    'apple_pay',
    'apple pay',
  ];

  methods.forEach((method) => {
    const lower = method.toLowerCase();
    if (!standardLogoMethods.includes(lower)) {
      const labelMap: Record<string, string> = {
        google_pay: 'Google Pay',
        stc_pay: 'STC Pay',
      };
      const label =
        labelMap[lower] ||
        method
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      const firstLetter = label.charAt(0).toUpperCase();

      icons.push(
        <div key={method} className="method-icon round-letter-logo" title={label}>
          {firstLetter}
        </div>,
      );
    }
  });

  return <div className="payment-methods-icons">{icons}</div>;
};

/**
 * Get gateway name helper
 * @param {any} key - The key
 */
const getGatewayName = (key: string) => {
  const names: Record<string, string> = {
    stripe: 'Stripe',
    hyperpay: 'HyperPay',
  };
  return names[key.toLowerCase()] || key;
};

/**
 * HyperPay logo component
 * @returns {any} React node
 */
function HyperPayLogoComponent() {
  return (
    <div className="hyperpay-logo-container">
      <span className="hyperpay-text-en">HyperPay</span>
      <span className="hyperpay-text-ar">هايبر باي</span>
    </div>
  );
}

/**
 * View Gateway side panel component
 * @param {any} props Component properties
 * @param {any} props.gateway - The gateway prop
 * @param {any} props.onEdit - The onEdit prop
 * @param {any} props.onClose - The onClose prop
 * @param {any} props.onStatusChange - The onStatusChange prop
 * @returns {any} React node
 */
function ViewGateway({
  gateway,
  onEdit,
  onClose,
  onStatusChange = () => {},
}: ViewGatewayProperties) {
  const dispatch = useAppDispatch();
  const { activePortal } = usePortal();
  const { t, i18n } = useTranslation('paymentGateway');
  const isCPO = activePortal === PortalType.NETWORKS;
  const [isActive, setIsActive] = useState<boolean>(gateway.isActive);
  const [activeUseCases, setActiveUseCases] = useState<string[]>(gateway.activeUseCases || []);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [pendingActiveState, setPendingActiveState] = useState<boolean | null>(null);
  const [selectedUseCases, setSelectedUseCases] = useState<string[]>([]);
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>({});
  const [isDefaultGateway, setIsDefaultGateway] = useState<boolean>(Boolean(gateway.isDefault));
  const [defaultCurrency, setDefaultCurrency] = useState<string>(
    gateway.isDefault
      ? gateway.defaultCurrency || gateway.currency || gateway.currencies?.[0] || ''
      : '',
  );
  const [currenciesList, setCurrenciesList] = useState<string[]>(
    gateway.currencies || (gateway.currency ? [gateway.currency] : []),
  );
  const [isUpdatingCurrency, setIsUpdatingCurrency] = useState<boolean>(false);
  const navigate = useNavigate();

  /**
   * Toggle visibility of credential secret fields
   * @param {any} fieldName The name of the credential field to toggle
   */
  const toggleFieldVisibility = (fieldName: string) => {
    setVisibleFields((previous) => ({
      ...previous,
      [fieldName]: !previous[fieldName],
    }));
  };

  const supportedUseCases = gateway.useCases || [];
  const totalCount = supportedUseCases.length;
  let activeCount = 0;
  if (isCPO) {
    if (isActive) {
      activeCount =
        activeUseCases.filter((uc: string) => supportedUseCases.includes(uc)).length || 0;
    }
  } else if (isActive) {
    activeCount = totalCount;
  }
  const inactiveCount = totalCount - activeCount;
  const useCasesToShow = ['b2c', 'b2b'].filter((uc) => supportedUseCases.includes(uc));

  let statusBadgeText = '';
  let statusBadgeClass = '';
  let showSubCounts = false;

  if (isCPO && totalCount > 1) {
    showSubCounts = true;
    if (activeCount === totalCount) {
      statusBadgeText = t('active');
      statusBadgeClass = 'active';
    } else if (activeCount > 0) {
      statusBadgeText = t('partiallyActive');
      statusBadgeClass = 'partially-active';
    } else {
      statusBadgeText = t('inactive');
      statusBadgeClass = 'inactive';
    }
  } else if (isActive) {
    statusBadgeText = t('active');
    statusBadgeClass = 'active';
  } else {
    statusBadgeText = t('inactive');
    statusBadgeClass = 'inactive';
  }

  const isToggleDisabled = isCPO
    ? !(
        permissionCheck('cpoPaymentGatewayStatusUpdate') ||
        permissionCheck('cpoPaymentGatewayUpdate')
      )
    : !(
        permissionCheck('mobilityPaymentGatewayStatusUpdate') ||
        permissionCheck('mobilityPaymentGatewayUpdate')
      );

  const isEditDisabled = isCPO
    ? !permissionCheck('cpoPaymentGatewayUpdate')
    : !permissionCheck('mobilityPaymentGatewayUpdate');

  useEffect(() => {
    setIsActive(gateway.isActive);
    setActiveUseCases(gateway.activeUseCases || []);
    setIsDefaultGateway(Boolean(gateway.isDefault));
    setDefaultCurrency(
      gateway.isDefault
        ? gateway.defaultCurrency || gateway.currency || gateway.currencies?.[0] || ''
        : '',
    );
    setCurrenciesList(gateway.currencies || (gateway.currency ? [gateway.currency] : []));
  }, [gateway]);

  const sortedCurrencies = useMemo(() => {
    if (!currenciesList || currenciesList.length === 0) return [];
    if (!isDefaultGateway || !defaultCurrency) return currenciesList;
    const defaultItem = currenciesList.find((currencyCode) => currencyCode === defaultCurrency);
    const otherItems = currenciesList.filter((currencyCode) => currencyCode !== defaultCurrency);
    return defaultItem ? [defaultItem, ...otherItems] : currenciesList;
  }, [currenciesList, defaultCurrency, isDefaultGateway]);

  /**
   * Set a currency as the default currency for the payment gateway
   * @param {string} currencyToSet - The selected currency code to set as default
   * @returns {Promise<void>} A promise that resolves when the update completes
   */
  const handleSetDefaultCurrency = async (currencyToSet: string): Promise<void> => {
    if ((isDefaultGateway && currencyToSet === defaultCurrency) || isUpdatingCurrency) return;
    const configId = gateway.configId || (gateway as any)._id;
    if (!configId) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('failedToUpdateDefaultCurrency'),
          type: 'error',
        }),
      );
      return;
    }

    setIsUpdatingCurrency(true);
    try {
      await paymentGatewayApi.setDefaultCurrency(configId, {
        defaultCurrency: currencyToSet,
      });

      const reorderedCurrencies = [
        currencyToSet,
        ...currenciesList.filter((currencyCode) => currencyCode !== currencyToSet),
      ];

      setIsDefaultGateway(true);
      setDefaultCurrency(currencyToSet);
      setCurrenciesList(reorderedCurrencies);
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('defaultCurrencyUpdated'),
          type: 'success',
        }),
      );
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message || error?.message || t('failedToUpdateDefaultCurrency'),
          type: 'error',
        }),
      );
    } finally {
      setIsUpdatingCurrency(false);
    }
  };

  const key = gateway.gatewayKey.toLowerCase();
  const isStripe = key === 'stripe';
  const isB2B = gateway.useCases?.[0]?.toUpperCase() === 'B2B';

  /**
   * Handle toggle switch click (open confirm modal)
   */
  const handleToggleClick = () => {
    setPendingActiveState(!isActive);
    setSelectedUseCases([]);
    setShowConfirm(true);
  };

  /**
   * Handle use case card click to toggle status
   * @param {any} useCase The use case string (b2c/b2b)
   * @param {any} isUseCaseActive The current active status of this use case
   */
  const handleCardClick = (useCase: string, isUseCaseActive: boolean) => {
    if (isToggleDisabled) return;

    if (isCPO) {
      setPendingActiveState(!isUseCaseActive);
      setSelectedUseCases([useCase]);
      setShowConfirm(true);
    } else {
      handleToggleClick();
    }
  };

  /**
   * Confirm toggle gateway status
   */
  const handleConfirmToggle = async () => {
    if (pendingActiveState === null) return;
    try {
      const type = pendingActiveState ? 'activate' : 'deactivate';
      const payload = isCPO ? { useCases: selectedUseCases } : undefined;
      const configId = gateway.configId || (gateway as any)._id || (gateway as any).id;
      await paymentGatewayApi.updatePaymentGatewayStatus(type, configId, payload);

      setIsActive(pendingActiveState);
      if (isCPO && selectedUseCases.length > 0) {
        if (pendingActiveState) {
          setActiveUseCases((previous) => Array.from(new Set([...previous, ...selectedUseCases])));
        } else {
          setActiveUseCases((previous) => previous.filter((uc) => !selectedUseCases.includes(uc)));
        }
      } else if (!isCPO) {
        setActiveUseCases(pendingActiveState ? supportedUseCases : []);
      }

      setShowConfirm(false);
      setPendingActiveState(null);
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (error: any) {
      console.log(error);
      setShowConfirm(false);
      setPendingActiveState(null);
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || error?.message || t('failedToUpdateStatus'),
          type: 'error',
        }),
      );
    }
  };

  /**
   * Cancel toggle gateway status
   */
  const handleCancelToggle = () => {
    setShowConfirm(false);
    setPendingActiveState(null);
  };

  /**
   * Handle checkbox toggle for use cases
   * @param {any} useCase - The useCase
   */
  const handleCheckboxChange = (useCase: string) => {
    setSelectedUseCases((previous) =>
      previous.includes(useCase) ? previous.filter((u) => u !== useCase) : [...previous, useCase],
    );
  };

  /**
   * Determine brand logo to display
   * @returns {React.ReactNode} The logo component or element
   */
  const renderLogo = () => {
    if (isStripe && isB2B) {
      // Figma mockup shows HyperPay logo for Stripe B2B Saudi Gateway
      return <HyperPayLogoComponent />;
    }
    if (isStripe) {
      return <StripeLogo className="gateway-logo-svg" />;
    }
    if (key === 'hyperpay') {
      return <HyperPayLogoComponent />;
    }
    return <div className="gateway-name">{getGatewayName(key)}</div>;
  };

  /**
   * Render platform fee detail row
   * @returns {React.ReactNode} The rendered platform fee node or null
   */
  const renderPlatformFee = (): React.ReactNode => {
    if (gateway.platformFeeType === 'amount') {
      if (gateway.platformFeeByCurrency && gateway.platformFeeByCurrency.length > 0) {
        const feeText = gateway.platformFeeByCurrency
          .map((item) => `${item.amount} ${item.currency}`)
          .join(', ');
        return (
          <div className="detail-row-v2">
            <span className="label">{t('platformFee')}</span>
            <span className="value">{feeText}</span>
          </div>
        );
      }
      if (gateway.platformFee !== undefined && gateway.platformFee !== '') {
        const unit = defaultCurrency || gateway.currencies?.[0] || gateway.currency || '';
        return (
          <div className="detail-row-v2">
            <span className="label">{t('platformFee')}</span>
            <span className="value">{`${gateway.platformFee} ${unit}`}</span>
          </div>
        );
      }
      return null;
    }

    if (gateway.platformFee !== undefined && gateway.platformFee !== '') {
      return (
        <div className="detail-row-v2">
          <span className="label">{t('platformFee')}</span>
          <span className="value">{`${gateway.platformFee}%`}</span>
        </div>
      );
    }

    return null;
  };

  // Config Details
  /**
   * Config
   * @returns {Array<{key: string, label: string, value: string, isSecret: boolean}>} List of credential field objects
   */
  const getCredentialFields = () => {
    const fields: { key: string; label: string; value: string; isSecret: boolean }[] = [];
    if (gateway.credentials) {
      Object.entries(gateway.credentials).forEach(([fieldName, value]) => {
        const generatedLabel = fieldName
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
          
        const label = t(fieldName, { defaultValue: generatedLabel });

        const isEndpoint =
          fieldName.toLowerCase().includes('endpoint') || fieldName.toLowerCase().includes('url');

        const isSecret = !isEndpoint;
        const isVisible = visibleFields[fieldName];
        const displayValue = isSecret && !isVisible ? '••••••••••••••••' : String(value);

        fields.push({ key: fieldName, label, value: displayValue, isSecret });
      });
    }
    return fields;
  };

  const configDetails = getCredentialFields();

  return (
    <div className={`view-gateway-container ${i18n.language === 'ar' ? 'rtl' : ''}`}>
      {/* HEADER */}
      <div className="side-panel-header-component">
        <div className="side-panel-header-left-title-actions">
          <div className="side-panel-title-wrapper">
            <p className="side-panel-header-title">
              {getGatewayName(gateway.gatewayKey)} {t('gatewayDetails')}
            </p>
            <div className="header-metadata-updated">
              <ClockIcon className="header-clock-icon" />
              <span>
                {t('lastUpdated')}: {new Date(gateway.updatedAt).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="side-panel-header-left-icons">
            {(isCPO
              ? permissionCheck('cpoPaymentTransactionView')
              : permissionCheck('mobilityPaymentTransactionView')) && (
              <div className="button-wrap">
                <span
                  role="presentation"
                  className="access-facility"
                  onClick={() =>
                    navigate(
                      `/ev-admin/configuration/PaymentGateway/transactions?gateway=${gateway.gatewayKey}`,
                    )
                  }>
                  <ExpandIcon className="icon" />
                </span>
                <div className="hover-label">{t('paymentTransactions')}</div>
              </div>
            )}
            {(isCPO
              ? permissionCheck('cpoPaymentGatewayUpdate')
              : permissionCheck('mobilityPaymentGatewayUpdate')) && (
              <div className="button-wrap">
                <span
                  role="presentation"
                  className="access-facility"
                  onClick={() => {
                    if (!isEditDisabled) {
                      onEdit();
                    }
                  }}>
                  <EditIcon className="icon" />
                </span>
                <div className="hover-label">{t('edit')}</div>
              </div>
            )}
          </div>
        </div>
        <CloseIcon className="icon close-btn" onClick={onClose} />
      </div>

      {/* STATUS & ACTIVATION ROW */}
      <div className="view-gateway-status-row">
        <div className="status-badge-container">
          <span className={`status-badge-v2 ${statusBadgeClass}`}>
            <span className="status-dot" />
            {statusBadgeText}
          </span>
          {showSubCounts && (
            <span className="status-sub-counts">
              {activeCount} {t('active')} • {inactiveCount} {t('inactive')}
            </span>
          )}
          <div className="activation-toggle-compact">
            <SwitchButton
              checked={isActive}
              change={handleToggleClick}
              disabled={isToggleDisabled}
            />
            <span className={`status-label ${isActive ? 'active' : 'inactive'}`}>
              {isActive ? t('active') : t('inactive')}
            </span>
          </div>
        </div>
        <div className="logo-box">{renderLogo()}</div>
      </div>
      <p className="activation-warning-note">{t('activationWarning')}</p>

      <div className="view-gateway-content">
        {/* GATEWAY DETAILS BLOCK */}
        <div className="details-card-block">
          <div className="detail-row-v2">
            <span className="label">{t('country')}</span>
            <span className="value">{getCountryName(gateway.country, t)}</span>
          </div>
          <div className="detail-row-v2">
            <span className="label">{t('paymentMethods')}</span>
            <span className="value">{renderPaymentMethods(gateway.paymentMethods)}</span>
          </div>
          {renderPlatformFee()}
          {gateway.paymentSurcharge !== undefined && gateway.paymentSurcharge !== '' && (
            <div className="detail-row-v2">
              <span className="label">{t('paymentSurcharge')}</span>
              <span className="value">{`${gateway.paymentSurcharge}%`}</span>
            </div>
          )}
        </div>

        {/* CURRENCIES SECTION */}
        {sortedCurrencies.length > 0 && (
          <div className="currencies-section">
            <h4 className="section-heading">{t('currencies')}</h4>
            <div className="currencies-card-block">
              {sortedCurrencies.map((currencyCode) => {
                const isCurrencyDefault = isDefaultGateway && currencyCode === defaultCurrency;
                return (
                  <div
                    key={currencyCode}
                    className={`currency-card-row ${isCurrencyDefault ? 'is-default' : ''}`}>
                    <div className="currency-info-col">
                      <span className="currency-code-pill">{currencyCode}</span>
                      {isCurrencyDefault && (
                        <span className="currency-default-badge">{t('default')}</span>
                      )}
                      {gateway.platformFeeType === 'amount' &&
                        gateway.platformFeeByCurrency &&
                        (() => {
                          const feeItem = gateway.platformFeeByCurrency.find(
                            (item) => item.currency === currencyCode,
                          );
                          if (
                            feeItem &&
                            feeItem.amount !== undefined &&
                            feeItem.amount !== ''
                          ) {
                            return (
                              <span className="currency-fee-badge">
                                {`${feeItem.amount} ${feeItem.currency}`}
                              </span>
                            );
                          }
                          return null;
                        })()}
                    </div>
                    <div className="currency-toggle-col">
                      <SwitchButton
                        checked={isCurrencyDefault}
                        disabled={isCurrencyDefault || isEditDisabled || isUpdatingCurrency}
                        change={() => handleSetDefaultCurrency(currencyCode)}
                      />
                      <span className={`toggle-label ${isCurrencyDefault ? 'active' : ''}`}>
                        {isCurrencyDefault ? t('default') : t('setDefault')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* BUSINESS USE CASES SECTION */}
        {useCasesToShow.length > 0 && (
          <div className="use-cases-section">
            <h4 className="use-cases-heading">{t('businessUseCases')}</h4>
            <div className="use-cases-grid">
              {useCasesToShow.map((useCase: string) => {
                const isSupported = gateway.useCases?.includes(useCase);
                if (!isSupported) return null;

                const isUseCaseActive = isCPO
                  ? !!(isActive && activeUseCases.includes(useCase))
                  : isActive;

                return (
                  <div
                    key={useCase}
                    className={`use-case-card ${isUseCaseActive ? 'active' : 'inactive'} ${!isToggleDisabled ? 'clickable' : ''}`}
                    onClick={() => handleCardClick(useCase, isUseCaseActive)}
                    role="presentation">
                    <div className="card-header-row">
                      <span className={`card-badge ${isUseCaseActive ? 'active' : 'inactive'}`}>
                        <span className={`badge-dot ${isUseCaseActive ? 'active' : 'inactive'}`} />
                        {isUseCaseActive ? t('active') : t('inactive')}
                      </span>
                    </div>
                    <h3 className="card-title">
                      {getUseCaseLabel(useCase, useCase.toUpperCase(), t)}
                    </h3>
                    <div className="card-body-row">
                      <div className="card-icon">
                        {isUseCaseActive ? (
                          <svg className="icon-success" viewBox="0 0 20 20" fill="none">
                            <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
                            <path
                              d="M6.5 10L9 12.5L13.5 7.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg className="icon-inactive" viewBox="0 0 20 20" fill="none">
                            <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
                            <line
                              x1="6.5"
                              y1="13.5"
                              x2="13.5"
                              y2="6.5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                      </div>
                      <p className="card-text">
                        {isUseCaseActive
                          ? t('paymentsActiveForUseCase')
                          : t('paymentsNotActiveForUseCase')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CONFIGURATION DETAILS BLOCK */}
        {configDetails.length > 0 && (
          <>
            <h4 className="section-heading">{t('configurationDetails')}</h4>
            <div className="details-card-block">
              {configDetails.map((item) => (
                <div className="detail-row-v2 align-center" key={item.key}>
                  <span className="label">{item.label}</span>
                  <span className="value text-break">{item.value}</span>
                  {item.isSecret && (
                    <button
                      type="button"
                      className="visibility-toggle-btn"
                      onClick={() => toggleFieldVisibility(item.key)}>
                      {visibleFields[item.key] ? (
                        <EyeSlashIcon className="eye-icon" />
                      ) : (
                        <EyeIcon className="eye-icon" />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* <div className="view-gateway-footer">
        <MyButton label="Test Configuration" buttonType="primary-outline" onClickFunc={() => {}} />
      </div> */}

      <ModalBox
        open={showConfirm}
        title={null}
        header={false}
        closeModal={handleCancelToggle}
        position="center"
        content={
          <div className="disable-warning-wrap">
            <div className="disable-waring-content">
              <h1>{t('warning')} ⚠️</h1>
              <p className="warning-text">
                <div className="warning-layout">
                  <span>
                    {pendingActiveState
                      ? t('activateGatewayConfirmation')
                      : t('deactivateGatewayConfirmation')}
                  </span>
                  {isCPO && (
                    <div className="use-case-select-container">
                      <span className="use-case-select-title">
                        {pendingActiveState
                          ? t('selectUseCasesToActivate')
                          : t('selectUseCasesToDeactivate')}
                      </span>
                      <div className="use-case-checkbox-group">
                        {gateway.useCases?.map((useCase) => (
                          <label
                            htmlFor={`usecase-${useCase}`}
                            key={useCase}
                            className="use-case-checkbox-label">
                            <input
                              id={`usecase-${useCase}`}
                              type="checkbox"
                              checked={selectedUseCases.includes(useCase)}
                              onChange={() => handleCheckboxChange(useCase)}
                            />
                            <span className="use-case-text">
                              {getUseCaseLabel(useCase, useCase, t)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </p>
            </div>
            <div className="disable-button">
              <MyButton
                label={t('cancel')}
                buttonType="back"
                onClickFunc={handleCancelToggle}
                disableHoverEffect
              />
              <MyButton
                label={pendingActiveState ? t('activate') : t('deactivate')}
                buttonType="warning"
                disableButton={isCPO && selectedUseCases.length === 0}
                onClickFunc={handleConfirmToggle}
              />
            </div>
          </div>
        }
      />
    </div>
  );
}
ViewGateway.defaultProps = {
  /**
   * onCancel default function
   */
  onStatusChange: () => {},
};

export default ViewGateway;
